import type { RecoilState } from "recoil"

export type ActionType = `ATTACH` | `CREATE` | `DELETE` | `DETACH` | `UPDATE`

export const INVERSE_ACTION_TYPE: Record<ActionType, ActionType> = {
  ATTACH: `DETACH`,
  CREATE: `DELETE`,
  DELETE: `CREATE`,
  DETACH: `ATTACH`,
  UPDATE: `UPDATE`,
}

export type Actions<
  FAMILY_NAMES extends string,
  FAMILY_NAME extends FAMILY_NAMES,
  FAMILY_MEMBER,
> = {
  CREATE: {
    type: `CREATE`
    family: FAMILY_NAME
    id: string
  }
  DELETE: {
    type: `DELETE`
    family: FAMILY_NAME
    id: string
  }
  UPDATE: {
    type: `UPDATE`
    family: FAMILY_NAME
    id: string
    payload: Partial<FAMILY_MEMBER>
  }
  ATTACH: {
    type: `ATTACH`
    family: `${FAMILY_NAME}_${FAMILY_NAMES}`
    id: string
    relatedId: string
  }
  DETACH: {
    type: `DETACH`
    family: `${FAMILY_NAME}_${FAMILY_NAMES}`
    id: string
    relatedId: string
  }
}

export type Changes<
  FAMILY_NAMES extends string,
  FAMILY_NAME extends FAMILY_NAMES,
  FAMILY_MEMBER,
  ACTIONS extends Actions<FAMILY_NAMES, FAMILY_NAME, FAMILY_MEMBER> = Actions<
    FAMILY_NAMES,
    FAMILY_NAME,
    FAMILY_MEMBER
  >,
> = {
  CREATE: {
    label: string
    redo: ACTIONS[`CREATE`]
    undo: ACTIONS[`DELETE`]
  }
  DELETE: {
    label: string
    redo: ACTIONS[`DELETE`]
    undo: ACTIONS[`CREATE`]
  }
  UPDATE: {
    label: string
    redo: ACTIONS[`UPDATE`]
    undo: ACTIONS[`UPDATE`]
  }
  ATTACH: {
    label: string
    redo: ACTIONS[`ATTACH`]
    undo: ACTIONS[`DETACH`]
  }
  DETACH: {
    label: string
    redo: ACTIONS[`DETACH`]
    undo: ACTIONS[`ATTACH`]
  }
}

export type ActionFor_<
  FAMILY_NAMES extends string,
  FAMILY_MEMBER = unknown,
  FAMILIES extends Record<
    FAMILY_NAMES,
    (id: string) => RecoilState<FAMILY_MEMBER>
  > = Record<FAMILY_NAMES, (id: string) => RecoilState<FAMILY_MEMBER>>,
> = {
  [FAMILY_NAME in FAMILY_NAMES]: Actions<
    FAMILY_NAMES,
    FAMILY_NAME,
    FAMILIES[FAMILY_NAME]
  >
}

export type ChangeFor_<
  FAMILY_NAMES extends string,
  FAMILY_MEMBER = unknown,
  FAMILIES extends Record<
    FAMILY_NAMES,
    (id: string) => RecoilState<FAMILY_MEMBER>
  > = Record<FAMILY_NAMES, (id: string) => RecoilState<FAMILY_MEMBER>>,
> = {
  [FAMILY_NAME in FAMILY_NAMES]: Changes<
    FAMILY_NAMES,
    FAMILY_NAME,
    FAMILIES[FAMILY_NAME]
  >
}

export type UnknownTargetActionTo_<
  FAMILY_NAMES extends string = string,
  FAMILY_MEMBER = unknown,
  FAMILIES extends Record<
    FAMILY_NAMES,
    (id: string) => RecoilState<FAMILY_MEMBER>
  > = Record<FAMILY_NAMES, (id: string) => RecoilState<FAMILY_MEMBER>>,
> = ActionFor_<FAMILY_NAMES, FAMILY_MEMBER, FAMILIES>[FAMILY_NAMES]

export type UnknownTargetChangeTo_<
  FAMILY_NAMES extends string = string,
  FAMILY_MEMBER = unknown,
  FAMILIES extends Record<
    FAMILY_NAMES,
    (id: string) => RecoilState<FAMILY_MEMBER>
  > = Record<FAMILY_NAMES, (id: string) => RecoilState<FAMILY_MEMBER>>,
> = ChangeFor_<FAMILY_NAMES, FAMILY_MEMBER, FAMILIES>[FAMILY_NAMES]

export type UnknownActionForUnknownTarget<
  FAMILY_NAMES extends string = string,
  FAMILY_MEMBER = unknown,
  FAMILIES extends Record<
    FAMILY_NAMES,
    (id: string) => RecoilState<FAMILY_MEMBER>
  > = Record<FAMILY_NAMES, (id: string) => RecoilState<FAMILY_MEMBER>>,
> = UnknownTargetActionTo_<FAMILY_NAMES, FAMILY_MEMBER, FAMILIES>[ActionType]

export type UnknownChangeForUnknownTarget<
  FAMILY_NAMES extends string = string,
  FAMILY_MEMBER = unknown,
  FAMILIES extends Record<
    FAMILY_NAMES,
    (id: string) => RecoilState<FAMILY_MEMBER>
  > = Record<FAMILY_NAMES, (id: string) => RecoilState<FAMILY_MEMBER>>,
> = UnknownTargetChangeTo_<FAMILY_NAMES, FAMILY_MEMBER, FAMILIES>[ActionType]

export type UpdateAction<FAMILY_NAMES extends string, FAMILY_MEMBER> = Actions<
  FAMILY_NAMES,
  FAMILY_NAMES,
  FAMILY_MEMBER
>[`UPDATE`]
export type DeleteAction<FAMILY_NAMES extends string, FAMILY_MEMBER> = Actions<
  FAMILY_NAMES,
  FAMILY_NAMES,
  FAMILY_MEMBER
>[`DELETE`]
export type CreateAction<FAMILY_NAMES extends string, FAMILY_MEMBER> = Actions<
  FAMILY_NAMES,
  FAMILY_NAMES,
  FAMILY_MEMBER
>[`CREATE`]

export const actionIsUpdate = <
  FAMILY_NAMES extends string = string,
  DATA extends Readonly<Record<FAMILY_NAMES, unknown>> = Readonly<
    Record<string, unknown>
  >,
>(
  action: UnknownActionForUnknownTarget,
): action is UpdateAction<FAMILY_NAMES, DATA> =>
  Boolean(action) && action.type === `UPDATE`

export const actionIsDelete = <
  FAMILY_NAMES extends string = string,
  FAMILIES extends Readonly<Record<FAMILY_NAMES, unknown>> = Readonly<
    Record<string, unknown>
  >,
>(
  action: UnknownActionForUnknownTarget,
): action is DeleteAction<FAMILY_NAMES, FAMILIES> =>
  Boolean(action) && action.type === `DELETE`

export const actionIsCreate = <
  FAMILY_NAMES extends string = string,
  FAMILIES extends Readonly<Record<FAMILY_NAMES, unknown>> = Readonly<
    Record<string, unknown>
  >,
>(
  action: UnknownActionForUnknownTarget,
): action is DeleteAction<FAMILY_NAMES, FAMILIES> =>
  Boolean(action) && action.type === `CREATE`
